// src/components/IndexTopHoldings.js
//
// Editorial hero view of an index and its top-weighted constituents.
// The story it tells: "how much of today's index move came from just 7 names?"
//
// Data:
//   Prices/history:  :6062/api/history?tickers=... (batch, cached, warm)
//   Company logos:   :8099/logos/{TICKER}.svg (rr_logo_ingest, TradingView-backed)
//
// Key metric — CONTRIBUTION ATTRIBUTION:
//   For each holding: contribution = (weight/100) × daily_pct_change
//   Sum across 7 = total attribution to the index
//   Divide by index's own %chg = share of move explained by top 7
//
//   Example: S&P +0.80% today, NVDA +3.2% at 7.5% weight → 0.24% attribution
//   Mag 7 combined attribution = 0.58% = 72% of the total index move.
//   That's the editorial hook.

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// ─── ENDPOINTS ──────────────────────────────────────────────
const INDICES_API = '/api/history';
const LOGO_HOST   = '/api/logos';

// ─── PALETTE ────────────────────────────────────────────────
const C = {
  bg: '#07070a', card: '#111118', surf: '#0d0d14',
  border: '#1e1e2a', borderSoft: '#1a1a24',
  gold: '#c9a227', purple: '#6660fe',
  red: '#f03e3e', green: '#34d399',
  amber: '#fbbf24', cyan: '#22d3ee',
  white: '#e8e8f0',
  dim: 'rgba(232,232,240,0.75)',
  dimmer: 'rgba(232,232,240,0.60)',
  faint: 'rgba(232,232,240,0.35)',
};

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,600;0,700;1,400&family=Fraunces:ital,wght@0,600;0,700;1,600&display=swap');
`;

// ─── HOLDINGS CONFIG ────────────────────────────────────────
// Weights are approximate constituent weightings (updated periodically).
// TODO(backlog): seed real weights from Yahoo Finance holdings API or
// the underlying ETF's holdings feed (e.g. SPY holdings for S&P 500,
// IVV, QQQ.SUMMARY, VOO). Would enable auto-refresh.
const INDEX_HOLDINGS = {
  '^GSPC': {
    label: 'S&P 500',
    subtitle: 'Mag 7 · big tech dominance',
    flag: 'us',
    editorialTag: 'MAG 7',
    holdings: [
      { ticker: 'NVDA',  name: 'NVIDIA',        weight: 7.5 },
      { ticker: 'MSFT',  name: 'Microsoft',     weight: 6.9 },
      { ticker: 'AAPL',  name: 'Apple',         weight: 6.4 },
      { ticker: 'AMZN',  name: 'Amazon',        weight: 4.0 },
      { ticker: 'META',  name: 'Meta',          weight: 2.8 },
      { ticker: 'GOOGL', name: 'Alphabet A',    weight: 2.3 },
      { ticker: 'TSLA',  name: 'Tesla',         weight: 1.7 },
    ],
  },
  '^NDX': {
    label: 'Nasdaq 100',
    subtitle: 'Top 7 · quase 50% do índice',
    flag: 'us',
    editorialTag: 'TOP 7',
    holdings: [
      { ticker: 'NVDA',  name: 'NVIDIA',        weight: 12.0 },
      { ticker: 'MSFT',  name: 'Microsoft',     weight: 8.5 },
      { ticker: 'AAPL',  name: 'Apple',         weight: 8.2 },
      { ticker: 'AMZN',  name: 'Amazon',        weight: 6.0 },
      { ticker: 'META',  name: 'Meta',          weight: 5.5 },
      { ticker: 'GOOGL', name: 'Alphabet A',    weight: 4.5 },
      { ticker: 'TSLA',  name: 'Tesla',         weight: 3.1 },
    ],
  },
  '^BVSP': {
    label: 'Ibovespa',
    subtitle: 'Blue chips brasileiros',
    flag: 'br',
    editorialTag: 'TOP 7',
    holdings: [
      { ticker: 'VALE3', name: 'Vale',           weight: 11.0 },
      { ticker: 'PETR4', name: 'Petrobras PN',   weight: 7.5 },
      { ticker: 'ITUB4', name: 'Itaú PN',        weight: 7.2 },
      { ticker: 'PETR3', name: 'Petrobras ON',   weight: 4.8 },
      { ticker: 'BBDC4', name: 'Bradesco PN',    weight: 3.8 },
      { ticker: 'ABEV3', name: 'Ambev',          weight: 3.4 },
      { ticker: 'WEGE3', name: 'WEG',            weight: 2.8 },
    ],
  },
  '^GDAXI': {
    label: 'DAX',
    subtitle: 'Top 7 · locomotivas alemãs',
    flag: 'de',
    editorialTag: 'TOP 7',
    holdings: [
      { ticker: 'SAP',    name: 'SAP',              weight: 15.0 },
      { ticker: 'SIE.DE', name: 'Siemens',          weight: 10.5 },
      { ticker: 'ALV.DE', name: 'Allianz',          weight: 7.8 },
      { ticker: 'DTE.DE', name: 'Deutsche Telekom', weight: 7.2 },
      { ticker: 'AIR.PA', name: 'Airbus',           weight: 5.4 },
      { ticker: 'MUV2.DE',name: 'Munich Re',        weight: 4.9 },
      { ticker: 'MBG.DE', name: 'Mercedes-Benz',    weight: 3.6 },
    ],
  },
};

// ─── UTILS ──────────────────────────────────────────────────
const fmtPrice = (v) => v == null || isNaN(v) ? '—' :
  Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtVar = (v) => v == null || isNaN(v) ? '—' :
  `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`;

const fmtVarSmall = (v) => v == null || isNaN(v) ? '—' :
  `${v >= 0 ? '+' : ''}${Number(v).toFixed(3)}%`;

// Stable pastel monogram color derived from ticker for fallback avatars
function monogramBg(ticker) {
  let h = 0;
  for (let i = 0; i < ticker.length; i++) {
    h = ((h << 5) - h + ticker.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 45%, 32%)`;
}

// ─── COMPANY LOGO ───────────────────────────────────────────
// Fetches SVG from :8099/logos/{TICKER}.svg. Falls back to a monogram
// avatar on 404 or network error. White background always so any logo
// (black-on-transparent, colored, etc.) renders visibly on the dark card.
const CompanyLogo = ({ ticker, name, size = 56 }) => {
  const [failed, setFailed] = useState(false);
  const baseTicker = (ticker || '').split('.')[0];

  const monogram = (name || ticker || '').replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || ticker.slice(0, 2);

  if (failed || !baseTicker) {
    return (
      <div style={{
        width: size, height: size,
        borderRadius: '50%',
        background: monogramBg(ticker),
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: 'white',
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: size * 0.34, fontWeight: 700, letterSpacing: '0.02em',
        border: `1px solid ${C.border}`,
        userSelect: 'none',
      }}>{monogram}</div>
    );
  }

  return (
    <div style={{
      width: size, height: size,
      borderRadius: '50%',
      background: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: size * 0.16, boxSizing: 'border-box',
      border: `1px solid ${C.border}`,
      overflow: 'hidden',
    }}>
      <img
        src={`${LOGO_HOST}/${baseTicker}.svg`}
        alt={name || ticker}
        onError={() => setFailed(true)}
        style={{
          width: '100%', height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </div>
  );
};

// ─── CONSTITUENT TILE ───────────────────────────────────────
const ConstituentTile = ({ holding, price, change, contribution, indexTicker }) => {
  const isPos = (change ?? 0) >= 0;
  const varColor = isPos ? C.green : C.red;
  const hasData = change != null;

  return (
    <div style={{
      padding: '16px 12px 14px',
      background: 'rgba(255,255,255,0.02)',
      border: `1px solid ${hasData ? (isPos ? 'rgba(52,211,153,0.25)' : 'rgba(240,62,62,0.25)') : C.border}`,
      borderRadius: 10,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 8,
      position: 'relative',
      transition: 'all 0.15s',
      minWidth: 0,
    }}>
      {/* Glow accent for the top 3 heaviest weights */}
      <CompanyLogo ticker={holding.ticker} name={holding.name} size={56} />

      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11, fontWeight: 700, color: C.white,
        letterSpacing: '0.03em', marginTop: 2,
      }}>{holding.ticker}</div>

      <div style={{
        fontFamily: "'Fraunces', Georgia, serif",
        fontSize: 10, fontStyle: 'italic',
        color: C.dim, textAlign: 'center', lineHeight: 1.2,
        maxWidth: '100%',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{holding.name}</div>

      {/* Daily change */}
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 13, fontWeight: 700, color: hasData ? varColor : C.faint,
        letterSpacing: '-0.01em',
      }}>
        {hasData ? `${isPos ? '▲' : '▼'} ${fmtVar(change)}` : '—'}
      </div>

      {/* Weight badge */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        marginTop: 2,
      }}>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 8, color: C.dimmer, letterSpacing: '0.12em',
        }}>PESO</span>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10, fontWeight: 700, color: C.gold,
          background: 'rgba(201,162,39,0.10)',
          border: '1px solid rgba(201,162,39,0.25)',
          padding: '1px 6px', borderRadius: 3,
        }}>{holding.weight.toFixed(1)}%</span>
      </div>

      {/* Contribution to index (weight × daily change) */}
      {contribution != null && (
        <div title="Contribuição ao movimento do índice hoje" style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9, fontWeight: 700, color: varColor,
          background: `${varColor}12`,
          border: `1px solid ${varColor}35`,
          padding: '2px 6px', borderRadius: 3,
          letterSpacing: '0.04em',
        }}>
          Δ {fmtVarSmall(contribution)}
        </div>
      )}
    </div>
  );
};

// ─── CONTRIBUTION BAR ───────────────────────────────────────
// Visual: the index changed X%. Of that, Y% came from the top 7.
// This is the editorial headline — puts the numbers in one line.
const ContributionBar = ({ indexLabel, indexChange, totalContribution, totalWeight }) => {
  if (indexChange == null || totalContribution == null) return null;

  // "Share explained": how much of index move is attributable to top 7
  // Note: this can exceed 100% or be negative if signs disagree (meaningful signal).
  const share = indexChange !== 0 ? (totalContribution / indexChange) * 100 : null;
  const absShare = share != null ? Math.min(Math.abs(share), 200) : 0; // cap visual at 200%
  const barColor = totalContribution >= 0 ? C.green : C.red;
  const shareColor = share != null && Math.sign(share) === Math.sign(indexChange) ? barColor : C.amber;

  return (
    <div style={{
      margin: '0 24px',
      padding: '16px 18px',
      background: 'linear-gradient(90deg, rgba(102,96,254,0.06), rgba(201,162,39,0.06))',
      border: '1px solid rgba(102,96,254,0.25)',
      borderRadius: 10,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 10, flexWrap: 'wrap', gap: 10,
      }}>
        <div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 8, letterSpacing: '0.16em', color: C.gold,
            fontWeight: 700, marginBottom: 4,
          }}>ATRIBUIÇÃO · TOP 7 → ÍNDICE</div>
          <div style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 15, fontStyle: 'italic', color: C.white, lineHeight: 1.3,
          }}>
            {share != null ? (
              <>
                <strong style={{ color: shareColor, fontFamily: "'IBM Plex Mono', monospace", fontStyle: 'normal', fontSize: 17 }}>
                  {share >= 0 ? '+' : ''}{share.toFixed(0)}%
                </strong>
                {' '}do movimento do {indexLabel} hoje veio dos 7 maiores pesos
              </>
            ) : (
              <>Índice sem variação — atribuição indeterminada.</>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 8, letterSpacing: '0.12em', color: C.dimmer,
          }}>PESO COMBINADO</div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 20, fontWeight: 700, color: C.gold,
            letterSpacing: '-0.02em',
          }}>{totalWeight.toFixed(1)}%</div>
        </div>
      </div>

      {/* Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9, color: C.dimmer, letterSpacing: '0.08em',
          minWidth: 72,
        }}>ÍNDICE {fmtVar(indexChange)}</div>
        <div style={{
          flex: 1, height: 6,
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 3, overflow: 'hidden',
          position: 'relative',
        }}>
          <div style={{
            width: `${Math.min(absShare, 100)}%`, height: '100%',
            background: `linear-gradient(90deg, ${barColor}88, ${barColor})`,
            transition: 'width 0.6s',
          }} />
          {absShare > 100 && (
            <div style={{
              position: 'absolute', top: 0, right: 0,
              width: `${Math.min(absShare - 100, 100)}%`, height: '100%',
              background: `repeating-linear-gradient(45deg, ${barColor}, ${barColor} 4px, ${barColor}88 4px, ${barColor}88 8px)`,
            }} />
          )}
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10, fontWeight: 700, color: barColor,
          letterSpacing: '0.04em',
          minWidth: 72, textAlign: 'right',
        }}>TOP 7 {fmtVar(totalContribution)}</div>
      </div>
    </div>
  );
};

// ─── DATA HOOK ─────────────────────────────────────────────
function useHoldingsData(activeIndex) {
  const [prices, setPrices] = useState({});   // ticker -> { last, prev, chg }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    const config = INDEX_HOLDINGS[activeIndex];
    if (!config) { setLoading(false); return; }

    const tickers = [activeIndex, ...config.holdings.map(h => h.ticker)];
    fetch(`${INDICES_API}/api/history?tickers=${encodeURIComponent(tickers.join(','))}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`http ${r.status}`)))
      .then(data => {
        if (!alive) return;
        const series = data.series || {};
        const p = {};
        Object.entries(series).forEach(([t, entry]) => {
          const c = (entry && entry.candles) || [];
          if (c.length >= 2) {
            const last = c[c.length - 1].close;
            const prev = c[c.length - 2].close;
            const chg = prev !== 0 ? ((last - prev) / prev) * 100 : null;
            p[t] = { last, prev, chg };
          } else if (c.length >= 1) {
            p[t] = { last: c[0].close, prev: null, chg: null };
          } else {
            p[t] = { last: null, prev: null, chg: null };
          }
        });
        setPrices(p);
        setLoading(false);
      })
      .catch(e => {
        if (!alive) return;
        setError(e.message);
        setLoading(false);
      });

    return () => { alive = false; };
  }, [activeIndex]);

  return { prices, loading, error };
}

// ─── MAIN ──────────────────────────────────────────────────
export const IndexTopHoldings = () => {
  const [activeIndex, setActiveIndex] = useState('^GSPC');
  const config = INDEX_HOLDINGS[activeIndex];
  const { prices, loading, error } = useHoldingsData(activeIndex);

  // Merge prices into holdings + compute contributions
  const { enrichedHoldings, indexPrice, indexChg, totalContribution, totalWeight } = useMemo(() => {
    if (!config) return { enrichedHoldings: [], indexPrice: null, indexChg: null, totalContribution: null, totalWeight: 0 };
    const idxP = prices[activeIndex];
    const idxChg = idxP?.chg ?? null;
    const enriched = config.holdings.map(h => {
      const p = prices[h.ticker];
      const chg = p?.chg ?? null;
      const contribution = chg != null ? (h.weight / 100) * chg : null;
      return { ...h, price: p?.last, change: chg, contribution };
    });
    const totalC = enriched.reduce((s, h) => s + (h.contribution || 0), 0);
    const totalW = config.holdings.reduce((s, h) => s + h.weight, 0);
    return {
      enrichedHoldings: enriched,
      indexPrice: idxP?.last ?? null,
      indexChg: idxChg,
      totalContribution: totalC,
      totalWeight: totalW,
    };
  }, [config, prices, activeIndex]);

  const isPos = (indexChg ?? 0) >= 0;
  const accentColor = isPos ? C.green : C.red;

  return (
    <div style={{
      background: 'linear-gradient(180deg, #0a0a0a 0%, #141414 100%)',
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      overflow: 'hidden',
      position: 'relative',
      fontFamily: "'IBM Plex Mono', monospace",
      color: C.white,
    }}>
      <style>{FONT_IMPORT}{`
        @keyframes rr-idx-shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .rr-idx-tab:hover { background: rgba(255,255,255,0.05) !important; }
      `}</style>

      {/* Grid backdrop */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.4,
        backgroundImage: `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`,
        backgroundSize: '32px 32px', pointerEvents: 'none',
      }} />

      {/* ── HEADER ── */}
      <div style={{
        padding: '18px 24px 14px',
        borderBottom: `1px solid ${C.border}`,
        position: 'relative',
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
      }}>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11, fontWeight: 800, color: C.purple,
          background: 'rgba(102,96,254,0.15)',
          border: '1px solid rgba(102,96,254,0.4)',
          padding: '4px 10px', borderRadius: 3,
          letterSpacing: '0.18em',
        }}>RR MAGMA</span>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 13, fontWeight: 800, color: C.white,
          letterSpacing: '0.08em',
        }}>QUEM CARREGA O ÍNDICE</span>
        <span style={{
          background: 'rgba(201,162,39,0.12)', color: C.gold,
          padding: '3px 10px', borderRadius: 3,
          fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
        }}>ATTRIBUTION LIVE</span>

        {/* Index picker (right side) */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {Object.entries(INDEX_HOLDINGS).map(([key, cfg]) => {
            const isActive = key === activeIndex;
            return (
              <button
                key={key}
                onClick={() => setActiveIndex(key)}
                className="rr-idx-tab"
                style={{
                  padding: '6px 12px', borderRadius: 5,
                  border: isActive ? `1px solid ${C.gold}66` : `1px solid ${C.border}`,
                  background: isActive ? 'rgba(201,162,39,0.10)' : 'transparent',
                  color: isActive ? C.gold : C.dimmer,
                  fontSize: 10, fontWeight: 700,
                  fontFamily: 'inherit', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                  letterSpacing: '0.05em',
                  transition: 'all 0.15s',
                }}
              >
                <img
                  src={`https://flagcdn.com/w20/${cfg.flag}.png`}
                  alt=""
                  style={{ width: 14, height: 'auto', borderRadius: 1 }}
                />
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── HERO ROW: title + big price + big %chg ── */}
      <div style={{
        padding: '22px 24px 18px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        gap: 20, flexWrap: 'wrap',
        borderBottom: `1px solid ${C.border}`,
        position: 'relative',
      }}>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <div style={{
            fontSize: 9, fontWeight: 700, color: accentColor,
            letterSpacing: '0.16em', marginBottom: 6,
          }}>{config.editorialTag} · {config.holdings.length} COMPONENTES</div>
          <h2 style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 32, fontWeight: 700, color: C.white,
            margin: 0, lineHeight: 1.1, letterSpacing: '-0.02em',
          }}>{config.label}</h2>
          <div style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontStyle: 'italic', fontSize: 13, color: C.dim,
            marginTop: 4,
          }}>{config.subtitle}</div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {indexPrice != null && (
            <>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 26, fontWeight: 700, color: C.white,
                lineHeight: 1, letterSpacing: '-0.02em',
              }}>{fmtPrice(indexPrice)}</div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 24, fontWeight: 800, color: accentColor,
                lineHeight: 1.1, letterSpacing: '-0.02em', marginTop: 4,
              }}>
                {indexChg != null ? `${isPos ? '▲' : '▼'} ${fmtVar(indexChg)}` : '—'}
              </div>
            </>
          )}
          {indexPrice == null && !loading && (
            <div style={{ fontSize: 11, color: C.faint }}>sem cotação</div>
          )}
        </div>
      </div>

      {/* ── CONSTITUENTS GRID ── */}
      <div style={{ padding: '20px 24px 16px', position: 'relative' }}>
        {loading ? (
          <div style={{
            padding: 40, textAlign: 'center',
            fontSize: 11, color: C.dimmer,
          }}>
            Carregando componentes via :6062/api/history batch...
          </div>
        ) : error ? (
          <div style={{
            padding: 24, textAlign: 'center',
            background: 'rgba(240,62,62,0.05)',
            border: '1px solid rgba(240,62,62,0.2)',
            borderRadius: 8,
            fontSize: 11, color: C.red,
          }}>⚠ {error}</div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(120px, 1fr))`,
            gap: 10,
          }}>
            {enrichedHoldings.map(h => (
              <ConstituentTile
                key={h.ticker}
                holding={h}
                price={h.price}
                change={h.change}
                contribution={h.contribution}
                indexTicker={activeIndex}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── CONTRIBUTION BAR ── */}
      {!loading && !error && (
        <div style={{ paddingBottom: 20 }}>
          <ContributionBar
            indexLabel={config.label}
            indexChange={indexChg}
            totalContribution={totalContribution}
            totalWeight={totalWeight}
          />
        </div>
      )}

      {/* ── FOOTER ── */}
      <div style={{
        margin: '0 24px 16px', padding: '8px 12px',
        background: 'rgba(245,200,66,0.05)',
        border: '1px solid rgba(245,200,66,0.1)',
        borderRadius: 4,
      }}>
        <p style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 8.5, color: 'rgba(245,200,66,0.6)',
          lineHeight: 1.45, margin: 0,
        }}>
          <strong style={{ color: C.amber }}>⚠ FONTE:</strong>{' '}
          Pesos aproximados atualizados periodicamente · logos via rr_logo_ingest (:8099)
          · preços via indices_etfs (:6062). Atribuição = peso × variação diária.
          Não constitui recomendação de investimento.
        </p>
      </div>
    </div>
  );
};

export default IndexTopHoldings;
